import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, X } from "lucide-react";
import IOSInstallGuide from "@/components/IOSInstallGuide";

interface InstallBannerProps {
  isInstalled: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  canPrompt: boolean;
  bannerDismissed: boolean;
  promptInstall: () => Promise<void>;
  dismissBanner: () => void;
  dismissIOSGuide: () => void;
}

const InstallBanner = ({
  isInstalled,
  isIOS,
  isAndroid,
  isDesktop,
  canPrompt,
  bannerDismissed,
  promptInstall,
  dismissBanner,
  dismissIOSGuide,
}: InstallBannerProps) => {
  const [dismissed, setDismissed] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  if (isInstalled || isDesktop || dismissed || bannerDismissed) return null;

  const handleDismiss = () => {
    dismissBanner();
    setDismissed(true);
  };

  const handleAndroidInstall = async () => {
    await promptInstall();
    setDismissed(true);
  };

  return (
    <>
      <div className="relative mb-4 rounded-xl border border-border bg-card p-4 shadow-sm animate-fade-in">
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground/50 hover:text-muted-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-4 pr-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
            <Smartphone className="h-5 w-5 text-accent" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Get the app experience</p>
            <p className="text-xs text-muted-foreground">
              Add Dearestly to your home screen for instant access.
            </p>
          </div>

          {isAndroid && (
            <Button
              size="sm"
              className="shrink-0 rounded-full"
              onClick={handleAndroidInstall}
              disabled={!canPrompt}
            >
              Add to Home Screen
            </Button>
          )}

          {isIOS && (
            <Button
              size="sm"
              className="shrink-0 rounded-full"
              onClick={() => setIosGuideOpen(true)}
            >
              How to Add
            </Button>
          )}
        </div>
      </div>

      <IOSInstallGuide
        open={iosGuideOpen}
        onOpenChange={setIosGuideOpen}
        onDismiss={dismissIOSGuide}
      />
    </>
  );
};

export default InstallBanner;
